/**
 * 订单数据CSV生成器
 * 根据要求生成指定格式和数量的订单数据，并导出为CSV文件
 */

import { saveAs } from 'file-saver';
import api from '@/api/index';

/**
 * 生成随机时间（在指定年月的任意一天的06:00-24:00时间段内）
 * @param {number} year - 年份
 * @param {number} month - 月份 (1-12)
 * @returns {string} 格式化的日期字符串 yyyy-MM-dd HH:mm:ss
 */
function getRandomTime(year, month) {
  // 获取该月的天数
  const daysInMonth = new Date(year, month, 0).getDate();
  const day = Math.floor(Math.random() * daysInMonth) + 1;
  const hour = 6 + Math.floor(Math.random() * 18); // 6-23点之间（06:00-24:00）
  const minute = Math.floor(Math.random() * 60);
  const second = Math.floor(Math.random() * 60);
  
  // 构建日期字符串，格式：yyyy-MM-dd HH:mm:ss
  const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
  
  return dateStr;
}

/**
 * 生成订单流水号
 * 格式：FK + 下单日期(YYYYMMDDHHmmss) + 随机数
 */
function generateFlowingNo(dateStr) {
  // 从日期字符串提取日期部分作为流水号的一部分
  const datePart = dateStr.replace(/[-:\/ ]/g, '').substring(0, 14); // 取前14位：YYYYMMDDHHmmss
  let randomPart = '';
  for (let i = 0; i < 10; i++) {
    randomPart += Math.floor(Math.random() * 10);
  }
  return `FK${datePart}${randomPart}`;
}

/**
 * 获取课程类目列表
 * @returns {Promise<Array>} 课程类目数组
 */
export async function fetchCourseCategories() {
  try {
    const response = await api.get('user/mechanismCategory/queryListPageChild', {
      status: 2,
      type: 2,
      source: '课程'
    });
    
    if (response.data && response.data.code === 0 && response.data.data) {
      return response.data.data.list || response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('获取课程类目失败:', error);
    return [];
  }
}

/**
 * 从类目列表中随机选择一个类目名称
 * @param {Array} categories - 类目列表
 * @returns {string} 类目名称
 */
function getRandomCategory(categories) {
  if (!categories || categories.length === 0) {
    return '运动类目';
  }
  const randomIndex = Math.floor(Math.random() * categories.length);
  const category = categories[randomIndex];
  return category.name || category.categoryName || category.title || '运动类目';
}

/**
 * 生成单个订单行数据对象（便于排序）
 * @param {number} amount - 金额
 * @param {string} rchargeType - 订单类型
 * @param {string} baseTitle - 订单基础标题
 * @param {number} year - 年份
 * @param {number} month - 月份
 * @param {Array} categories - 课程类目列表
 * @returns {Object} 订单数据对象
 */
export function generateOrderRowObject(amount, rchargeType, baseTitle, year, month, categories) {
  const user_id = Math.floor(Math.random() * 1001) + 4000; // 4000-5000
  const mechanism_id = Math.floor(Math.random() * 49) + 272; // 272-320
  const create_time = getRandomTime(year, month);
  const finished_time = create_time; // 完成时间与创建时间相同
  const rcharge_time = create_time;
  const flowing_no = generateFlowingNo(create_time);
  
  // 从类目中随机选择一个，拼接成标题
  const categoryName = getRandomCategory(categories);
  let title = `${categoryName}${baseTitle}`;
  if (rchargeType == 'member') {
    title = '会员卡';
  }
  
  return {
    user_id,
    source: 'sport',
    amount,
    amount_payable: amount,
    flowing_no,
    create_time,
    finished_time,
    rcharge_type: rchargeType,
    finished: 1,
    status: 2,
    mechanism_id,
    title,
    type: 'v',
    rcharge_time,
    pay_type: 'wx'
  };
}

/**
 * 将订单对象转换为CSV行字符串
 * @param {Object} order - 订单对象
 * @returns {string} CSV行字符串
 */
export function convertOrderToCsvRow(order) {
  const row = [
    order.user_id,
    order.source,
    order.amount,
    order.amount_payable,
    order.flowing_no,
    order.create_time,
    order.finished_time,
    order.rcharge_type,
    order.finished,
    order.status,
    order.mechanism_id,
    order.title,
    order.type,
    order.rcharge_time,
    order.pay_type
  ];
  
  // 将每个字段用引号包裹（如果需要），并用逗号连接
  return row.map(field => {
    if (typeof field === 'string') {
      // 如果字符串包含逗号或引号，需要特殊处理
      if (field.includes(',') || field.includes('"')) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return `"${field}"`;
    }
    return field;
  }).join(',');
}

/**
 * 按时间升序排序订单数组
 * @param {Array} orders - 订单对象数组
 * @returns {Array} 排序后的订单数组
 */
export function sortOrdersByTime(orders) {
  return orders.sort((a, b) => {
    return new Date(a.create_time) - new Date(b.create_time);
  });
}

/**
 * 内部函数：生成订单对象数组（供批量导出使用）
 * @param {Object} options - 配置选项
 * @returns {Array} 订单对象数组
 */
export function generateOrderCsvDataInternal(options = {}) {
  const {
    year = 2022,
    month = 7,
    coachOrderCount = 1200,
    storeOrderCount = 300,
    priceDistribution = [
      { amount: 9.9, count: 938 },
      { amount: 99, count: 351 },
      { amount: 199, count: 141 },
      { amount: 1680, count: 47 },
      { amount: 2480, count: 23 }
    ],
    memberOrderCount = 38,
    memberPrice = 698,
    categories = []
  } = options;
  
  // 存储所有订单对象的数组
  const allOrders = [];
  
  // 定义价格和标题映射
  const priceTitleMap = {
    9.9: '体验课',
    99: '基础课包',
    199: '进阶课包',
    1680: '进阶课包',
    2480: '进阶课包'
  };
  
  // 生成教练订单（need_full）
  let coachOrdersGenerated = 0;
  for (const priceConfig of priceDistribution) {
    // 计算该价格在教练订单中的数量（按比例分配）
    const totalRefCount = 1500; 
    const ratio = priceConfig.count / totalRefCount;
    const countForCoach = Math.round(ratio * coachOrderCount);
    
    for (let i = 0; i < countForCoach && coachOrdersGenerated < coachOrderCount; i++) {
      const baseTitle = priceTitleMap[priceConfig.amount] || '课程';
      const order = generateOrderRowObject(priceConfig.amount, 'need_full', baseTitle, year, month, categories);
      allOrders.push(order);
      coachOrdersGenerated++;
    }
  }
  
  // 补足教练订单到指定数量
  while (coachOrdersGenerated < coachOrderCount) {
    const randomPrice = priceDistribution[Math.floor(Math.random() * priceDistribution.length)];
    const baseTitle = priceTitleMap[randomPrice.amount] || '课程';
    const order = generateOrderRowObject(randomPrice.amount, 'need_full', baseTitle, year, month, categories);
    allOrders.push(order);
    coachOrdersGenerated++;
  }
  
  // 生成门店订单（study_card）
  let storeOrdersGenerated = 0;
  for (const priceConfig of priceDistribution) {
    // 计算该价格在门店订单中的数量（按比例分配）
    const totalRefCount = 1500;
    const ratio = priceConfig.count / totalRefCount;
    const countForStore = Math.round(ratio * storeOrderCount);
    
    for (let i = 0; i < countForStore && storeOrdersGenerated < storeOrderCount; i++) {
      const baseTitle = priceTitleMap[priceConfig.amount] || '课程';
      const order = generateOrderRowObject(priceConfig.amount, 'study_card', baseTitle, year, month, categories);
      allOrders.push(order);
      storeOrdersGenerated++;
    }
  }
  
  // 补足门店订单到指定数量
  while (storeOrdersGenerated < storeOrderCount) {
    const randomPrice = priceDistribution[Math.floor(Math.random() * priceDistribution.length)];
    const baseTitle = priceTitleMap[randomPrice.amount] || '课程';
    const order = generateOrderRowObject(randomPrice.amount, 'study_card', baseTitle, year, month, categories);
    allOrders.push(order);
    storeOrdersGenerated++;
  }
  
  // 生成会员卡订单（member）
  for (let i = 0; i < memberOrderCount; i++) {
    const order = generateOrderRowObject(memberPrice, 'member', '会员卡', year, month, categories);
    allOrders.push(order);
  }
  
  // 按时间升序排序
  return sortOrdersByTime(allOrders);
}

/**
 * 生成CSV数据
 * @param {Object} options - 配置选项
 * @param {number} options.year - 年份，默认2022
 * @param {number} options.month - 月份，默认7
 * @param {number} options.coachOrderCount - 教练订单数量，默认1200
 * @param {number} options.storeOrderCount - 门店订单数量，默认300
 * @param {Array} options.priceDistribution - 价格分布配置
 * @param {number} options.memberOrderCount - 会员卡订单数量，默认38
 * @param {number} options.memberPrice - 会员卡价格，默认698
 * @param {Array} options.categories - 课程类目列表
 * @returns {string} CSV格式的字符串
 */
export function generateOrderCsvData(options = {}) {
  const sortedOrders = generateOrderCsvDataInternal(options);
  
  // CSV头部
  const headers = [
    'user_id',
    'source',
    'amount',
    'amount_payable',
    'flowing_no',
    'create_time',
    'finished_time',
    'rcharge_type',
    'finished',
    'status',
    'mechanism_id',
    'title',
    'type',
    'rcharge_time',
    'pay_type'
  ];
  
  // 构建CSV内容
  const csvRows = [headers.join(',')];
  
  // 将排序后的订单转换为CSV行
  sortedOrders.forEach(order => {
    csvRows.push(convertOrderToCsvRow(order));
  });
  
  return csvRows.join('\n');
}

/**
 * 导出CSV文件
 * @param {Object} options - 配置选项（同generateOrderCsvData）
 * @param {string} filename - 导出的文件名
 */
export async function exportOrderCsv(options = {}, filename = null) {
  // 如果没有提供类目，先获取
  if (!options.categories || options.categories.length === 0) {
    options.categories = await fetchCourseCategories();
  }
  
  const csvContent = generateOrderCsvData(options);
  
  // 生成默认文件名
  if (!filename) {
    const { year = 2022, month = 7 } = options;
    filename = `order_data_${year}_${month.toString().padStart(2, '0')}.csv`;
  }
  
  // 添加BOM头以支持中文
  const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, filename);
}